'use client'

import { useMemo, useRef } from 'react'

import { NestableAnalyticsProvider } from '@b-stock/fe-core/bstock-react'

import AuctionCard from '@/components/common/auctionCard/AuctionCard'
import AuctionCardSkeleton from '@/components/common/auctionCard/AuctionCardSkeleton'
import Carousel from '@/components/common/carousel/Carousel'
import {
  Section,
  Container,
  Header,
  TitleGroup,
  TitleRow,
  Title,
  Subtitle,
  Count,
  ViewAllButton,
  ViewAllButtonMobile,
  IconWrapper,
} from '@/components/common/carousel/ui'
import { ItemListProvider } from '@/helpers/telemetry/itemListContext'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import { useViewItemListOnVisible } from '@/helpers/telemetry/useViewItemListOnVisible'

import { useCarouselImpressionTracking } from '../hooks/useCarouselImpressionTracking'
import type { BaseCarouselProps } from './types'

export function BaseCarousel({
  data,
  isLoading,
  title,
  subtitle,
  icon,
  viewMoreUrl,
  viewMoreText = 'View All',
  onViewAllClick,
  carouselName,
  screenName = 'homepage',
  showMobileButton = false,
  enableGTMTracking = true,
  sort_type,
  carousel_variant,
  model_version,
  renderCardExtra,
}: BaseCarouselProps) {
  const { track } = useAnalytics()
  const containerRef = useRef<HTMLDivElement>(null)

  const listings = useMemo(() => data?.listings || [], [data?.listings])
  const totalCount = data?.total ?? 0

  const impressionId = useCarouselImpressionTracking({
    listings,
    carouselName,
    screenName,
    track,
    sort_type,
    carousel_variant,
    model_version,
  })

  const itemListId = `carousel-${carouselName ?? 'default'}-homepage`
  const itemListName = typeof title === 'string' ? title : 'Carousel'

  useViewItemListOnVisible({
    containerRef,
    listings,
    itemListId,
    itemListName,
    enabled: enableGTMTracking && !isLoading && listings.length > 0,
  })

  const analyticsData = useMemo(
    () => ({
      listing_card_impression_id: impressionId.current,
      ...(sort_type !== undefined && { sort_type }),
      ...(carousel_variant !== undefined && { carousel_variant }),
      ...(model_version !== undefined && { model_version }),
    }),
    // oxlint-disable-next-line react/preserve-manual-memoization
    [impressionId, sort_type, carousel_variant, model_version]
  )

  if (isLoading) {
    return (
      <Section>
        <Container>
          <Header>
            <TitleRow>
              {icon && <IconWrapper>{icon}</IconWrapper>}
              <Title>{title}</Title>
            </TitleRow>
          </Header>
          <Carousel minItemsToShowButtons={2}>
            {Array(10)
              .fill(null)
              .map((_, index) => (
                <AuctionCardSkeleton key={`skeleton-${index + 1}`} />
              ))}
          </Carousel>
        </Container>
      </Section>
    )
  }

  if (listings.length === 0) {
    return null
  }

  const content = (
    <Section ref={enableGTMTracking ? containerRef : undefined}>
      <NestableAnalyticsProvider value={analyticsData}>
        <Container>
          <Header>
            <TitleGroup>
              <TitleRow>
                {icon && <IconWrapper>{icon}</IconWrapper>}
                <Title>{title}</Title>
                <Count>
                  {totalCount} {totalCount === 1 ? 'item' : 'items'}
                </Count>
              </TitleRow>
              {subtitle && (
                <Subtitle data-testid="carousel-subtitle">{subtitle}</Subtitle>
              )}
            </TitleGroup>
            {viewMoreUrl && (
              <ViewAllButton
                size="large"
                appearance="tertiary"
                href={viewMoreUrl}
                onClick={onViewAllClick}
              >
                {viewMoreText}
              </ViewAllButton>
            )}
          </Header>

          <Carousel minItemsToShowButtons={2}>
            {listings.map((listing, index) => (
              <AuctionCard
                key={listing.id}
                listing={listing}
                position={index + 1}
                carousel_name={carouselName}
                screenName={screenName}
                extra={renderCardExtra?.(listing)}
              />
            ))}
          </Carousel>
          {showMobileButton && viewMoreUrl && (
            <ViewAllButtonMobile
              size="large"
              appearance="tertiary"
              href={viewMoreUrl}
              onClick={onViewAllClick}
            >
              {viewMoreText}
            </ViewAllButtonMobile>
          )}
        </Container>
      </NestableAnalyticsProvider>
    </Section>
  )

  if (enableGTMTracking) {
    return (
      <ItemListProvider itemListId={itemListId} itemListName={itemListName}>
        {content}
      </ItemListProvider>
    )
  }

  return content
}
